import { useState, useEffect, useRef, useCallback } from "react";
import { animated, useSpring } from "@react-spring/web";
import { useRecordEvent } from "app/hook";
import { AnalyticsEventType } from "app/types";
import i18n from "common/i18n";
import { shuffle, usePlaySound } from "common/utils";

const LONG_PRESS_DURATION = 1150;
const NUMBER_COUNT = 6;
const BUTTON_SIZE = 90;

const KANJI_NUMBERS = ["零", "一", "二", "三", "四", "五", "六", "七", "八", "九"];

const generateNumbers = (): number[] => {
  const all = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];
  return shuffle(all).slice(0, NUMBER_COUNT);
};

const pickTarget = (numbers: number[]): number => {
  const index = Math.floor(Math.random() * numbers.length);
  return numbers[index];
};

export const Shape = (props: { setSuccess: (success: boolean) => void }) => {
  const play = usePlaySound();
  const handleRecordEvent = useRecordEvent();
  const [numbers, setNumbers] = useState<number[]>(() => generateNumbers());
  const [target, setTarget] = useState<number>(() => pickTarget(numbers));
  const [activeNumber, setActiveNumber] = useState<number | null>(null);
  const [progress, setProgress] = useState(0);
  const startTimeRef = useRef<number>(0);
  const rafRef = useRef<number>(0);
  const succeededRef = useRef(false);

  const [animationStyles, animationApi] = useSpring(() => ({
    from: { x: 0 },
  }));

  const resetPuzzle = useCallback(() => {
    const newNumbers = generateNumbers();
    setNumbers(newNumbers);
    setTarget(pickTarget(newNumbers));
    setActiveNumber(null);
    setProgress(0);
  }, []);

  const shakeAndReset = useCallback(() => {
    animationApi.start({
      to: async (next) => {
        await next({ x: 10 });
        await next({ x: -10 });
        await next({ x: 10 });
        await next({ x: -10 });
        await next({ x: 0 });
        resetPuzzle();
      },
      config: { duration: 100 },
    });
  }, [animationApi, resetPuzzle]);

  const updateProgress = useCallback(() => {
    if (startTimeRef.current === 0) return;
    const elapsed = performance.now() - startTimeRef.current;
    const ratio = Math.min(elapsed / LONG_PRESS_DURATION, 1);
    setProgress(ratio);

    if (ratio >= 1) {
      succeededRef.current = true;
      play();
      props.setSuccess(true);
      return;
    }
    rafRef.current = requestAnimationFrame(updateProgress);
  }, [play, props]);

  const handlePointerDown = useCallback(
    (num: number) => () => {
      if (succeededRef.current) return;

      if (num !== target) {
        play();
        handleRecordEvent(AnalyticsEventType.PARENT_GATE_FAIL);
        shakeAndReset();
        return;
      }

      setActiveNumber(num);
      startTimeRef.current = performance.now();
      rafRef.current = requestAnimationFrame(updateProgress);
    },
    [target, play, shakeAndReset, updateProgress],
  );

  const handlePointerUp = useCallback(() => {
    if (succeededRef.current) return;
    cancelAnimationFrame(rafRef.current);
    startTimeRef.current = 0;

    if (activeNumber !== null && progress < 1) {
      handleRecordEvent(AnalyticsEventType.PARENT_GATE_FAIL);
      setActiveNumber(null);
      setProgress(0);
      shakeAndReset();
    }
  }, [activeNumber, progress, shakeAndReset]);

  useEffect(() => {
    return () => {
      cancelAnimationFrame(rafRef.current);
    };
  }, []);

  const topRow = numbers.slice(0, 3);
  const bottomRow = numbers.slice(3, 6);

  const getButtonStyle = (num: number) => {
    const isActive = activeNumber === num;
    const isSuccess = succeededRef.current && num === target;

    if (isSuccess) {
      return {
        background: "#2bdc98",
        border: "3px solid #1fb078",
        boxShadow: "0 3px 0 0 #167752, 0 0 16px rgba(43, 220, 152, 0.4)",
        color: "#FFFFFF",
        transform: "scale(1.08)",
      };
    }
    if (isActive) {
      return {
        background: "#FFA03B",
        border: "3px solid #E08A2E",
        boxShadow: "0 3px 0 0 #C07828",
        color: "#FFFFFF",
        transform: "translateY(0px)",
      };
    }
    return {
      background: "#FFFFFF",
      border: "3px solid #E0E4E7",
      boxShadow: "0 3px 0 0 #D0D4D7",
      color: "#3F4A61",
      transform: "translateY(0px)",
    };
  };

  return (
    <animated.div
      style={{
        width: "100%",
        height: "100%",
        ...animationStyles,
      }}
    >
      <div className="w-full h-full flex-col-center p-[24px]">
        {/* 説明テキスト */}
        <div className="flex-col-center mb-[8px]">
          <p className="text !text-[20px] !leading-[28px] text-[#6B7280]">
            {i18n
              .t("MSG_PARENT_GATE_LONGPRESS_INSTRUCTION", {
                number: "",
              })
              .replace("「」", "")
              .trim()}
          </p>
          <p className="text !text-[40px] !leading-[52px] mt-[4px]" style={{ color: "#FFA03B" }}>
            「{KANJI_NUMBERS[target]}」
          </p>
        </div>

        {/* プログレスバー */}
        <div className="flex-col-el w-[320px] h-[12px] bg-[#ede3cf] rounded-full mt-[8px] mb-[20px] overflow-hidden">
          <div
            className="h-full rounded-full"
            style={{
              width: `${progress * 100}%`,
              background: progress >= 1 ? "#2bdc98" : "linear-gradient(90deg, #FFB96E, #FFA03B)",
              transition: progress === 0 ? "none" : undefined,
            }}
          />
        </div>

        {/* 数字ボタン */}
        <div
          className="flex-col-el flex-center flex-1 pointer-events-auto"
          style={{ touchAction: "none" }}
        >
          {[topRow, bottomRow].map((row, rowIndex) => (
            <div key={rowIndex} className="flex-row-center gap-[28px] mb-[16px]">
              {row.map((num) => (
                <button
                  key={num}
                  disabled={succeededRef.current}
                  className="relative flex items-center justify-center rounded-full cursor-pointer select-none"
                  style={{
                    width: BUTTON_SIZE,
                    height: BUTTON_SIZE,
                    ...getButtonStyle(num),
                    transition: "all 0.15s ease-out",
                    WebkitTapHighlightColor: "transparent",
                  }}
                  onPointerDown={handlePointerDown(num)}
                  onPointerUp={handlePointerUp}
                  onPointerLeave={handlePointerUp}
                >
                  {succeededRef.current && num === target ? (
                    <span
                      className="material-symbols-outlined"
                      style={{ fontSize: 40, color: "#FFFFFF" }}
                    >
                      check
                    </span>
                  ) : (
                    <span
                      className="text-body"
                      style={{
                        fontSize: 34,
                        lineHeight: 1,
                        color: "inherit",
                      }}
                    >
                      {num}
                    </span>
                  )}
                </button>
              ))}
            </div>
          ))}
        </div>
      </div>
    </animated.div>
  );
};
